import React, { useEffect, useMemo, useState } from "react";
import { useDraftMeetings } from "../../../../context/DraftMeetingContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { momentLocalizer, Views, Calendar } from "react-big-calendar";
import moment from "moment";
import ReactCalendarToolbar from "./ReactCalendarToolbar";
import CustomEvent from "../../Meeting/CustomeEvent";
import NewMeetingModal from "../../Meeting/CreateNewMeeting/NewMeetingModal";
import { useFormContext } from "../../../../context/CreateMeetingContext";

const EventWrapper = ({ children }) => {
  return <div style={{ marginBottom: "2px", width: "100%" }}>{children}</div>;
};

const CustomDateHeader = ({ label, date, localizer }) => {
  return <div>{moment(date).format("ddd D MMM")}</div>;
};
const CustomAgendaHeader = ({ label }) => {
  return null;
};

const ReactCalendar = ({ meetings, from }) => {
  const { open, handleShow, setMeeting, handleCloseModal } = useFormContext();
  const [t] = useTranslation("global");
  const navigate = useNavigate();

  const [myEventsList, setMyEventsList] = useState([]);

  const [overlappingSlots, setOverlappingSlots] = useState(new Set());
  const [currentStartDate, setCurrentStartDate] = useState(
    moment().startOf("week").toDate()
  );
  const [currentView, setCurrentView] = useState(Views.WEEK); // Track current view
  const { language } = useDraftMeetings();
  const [earliestHour, setEarliestHour] = useState(9);
  const [latestHour, setLatestHour] = useState(17);
  useEffect(() => {
    moment.locale(language);
  }, [language]);
  const localizer = momentLocalizer(moment);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);

  useEffect(() => {
    const currentDate = new Date();
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(currentDate);
    endOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    setMin(startOfWeek);
    setMax(endOfWeek);
  }, []);

  const minTime = useMemo(() => {
    const date = new Date();
    const hour =
      Number.isInteger(earliestHour) && earliestHour >= 0 && earliestHour <= 23
        ? earliestHour
        : 9;
    date.setHours(hour, 0, 0);
    return date;
  }, [earliestHour, currentStartDate]);

  const maxTime = useMemo(() => {
    const date = new Date();
    const hour =
      Number.isInteger(latestHour) && latestHour >= 0 && latestHour <= 23
        ? latestHour
        : 17;
    date.setHours(hour, 59, 59);
    return date;
  }, [latestHour, currentStartDate]);

  const getFormattedAbortDateTime = (abortDateTime, timezone) => {
    const userTimezone = moment.tz.guess();
    return moment
      .tz(abortDateTime, "YYYY-MM-DD HH:mm:ss", timezone || "UTC")
      .tz(userTimezone)
      .toDate();
  };
  const transformMeetingToEvent = (meeting) => {
    if (
      ![
        "active",
        "closed",
        "abort",
        "in_progress",
        "to_finish",
        "todo",
      ].includes(meeting.status)
    )
      return null;

    let start, end;

    const userTimezone = moment.tz.guess();
    const meetingTimezone = meeting?.timezone || "UTC";
    if (meeting.status === "abort") {
      start = moment
        .tz(
          `${meeting?.date} ${meeting?.starts_at}`,
          "YYYY-MM-DD HH:mm:ss",
          meetingTimezone
        )
        .tz(userTimezone)
        .toDate();
      end = getFormattedAbortDateTime(
        meeting?.abort_end_time,
        meeting?.timezone
      );
    } else if (
      meeting.status === "closed" ||
      meeting.status === "in_progress" ||
      meeting?.status === "to_finish" ||
      meeting?.status === "todo"
    ) {
      start = moment
        .tz(
          `${meeting?.date} ${meeting?.starts_at}`,
          "YYYY-MM-DD HH:mm:ss",
          meetingTimezone
        )
        .tz(userTimezone)
        .toDate();

      const estimateDate =
        meeting.estimate_time?.split("T")[0] ||
        meeting.estimate_time?.split(" ")[0];
      const estimateTime =
        meeting.estimate_time?.split("T")[1] ||
        meeting.estimate_time?.split(" ")[1] ||
        "00:00:00";
      end = moment
        .tz(
          `${estimateDate} ${estimateTime}`,
          "YYYY-MM-DD HH:mm:ss",
          meetingTimezone
        )
        .tz(userTimezone)
        .toDate();
    } else {
      start = moment
        .tz(
          `${meeting?.date} ${meeting?.start_time}`,
          "YYYY-MM-DD HH:mm:ss",
          meetingTimezone
        )
        .tz(userTimezone)
        .toDate();
      const estimateDate =
        meeting.estimate_time?.split("T")[0] ||
        meeting.estimate_time?.split(" ")[0];
      const estimateTime =
        meeting.estimate_time?.split("T")[1] ||
        meeting.estimate_time?.split(" ")[1] ||
        "00:00:00";
      end = moment
        .tz(
          `${estimateDate} ${estimateTime}`,
          "YYYY-MM-DD HH:mm:ss",
          meetingTimezone
        )
        .tz(userTimezone)
        .toDate();
    }

    return {
      id: meeting.id,
      title: meeting.title,
      start: start,
      end: end,
      allDay: false,
      resource: meeting,
      steps: meeting?.steps,
    };
  };

  const eventPropGetter = (event) => {
    const status = event.resource?.status;
    const timezone = event.resource?.timezone || "Europe/Paris"; // Default timezone if not provided
    const now = moment().utcOffset(timezone);
    const isPast = moment(event.end).isBefore(now) && status !== "in_progress";

    let borderColor = "#ccc";
    let borderWidth = "2px";
    let backgroundColor = "#fff";
    let zIndex = 1;

    const isSelected = selectedEvent && selectedEvent.id === event.id;

    if (isSelected) {
      backgroundColor = "#f0f7ff";
      borderColor = "#0066cc";
      borderWidth = "3px";
    } else if (status === "active") {
      const meetingDateTime = moment(
        `${event.resource.date}T${event.resource.start_time}`
      ).utcOffset(timezone);
      const isFutureMeeting = meetingDateTime.isAfter(now);

      borderColor = isFutureMeeting ? "rgb(91, 170, 234)" : "red";
      borderWidth = "5px";
    } else if (status === "in_progress") {
      borderColor = "yellow";
      borderWidth = "5px";
    } else if (status === "to_finish") {
      borderColor = "#ff9800";
      borderWidth = "5px";
    } else if (status === "todo") {
      borderColor = "#6c757d";
      borderWidth = "5px";
    } else if (status === "closed") {
      borderColor = "rgb(119, 214, 113)";
      borderWidth = "5px";
    } else if (status === "abort") {
      borderColor = "rgb(119, 19, 241)";
      borderWidth = "5px";
    }

    return {
      style: {
        borderLeft: `${borderWidth} solid ${borderColor}`,
        backgroundColor: backgroundColor,
        paddingLeft: "8px",
        borderRadius: "4px",
        color: "#000000",
        opacity: 1,
        boxShadow: isSelected ? "0 0 5px rgba(0, 102, 204, 0.5)" : "none",
        zIndex: zIndex,
      },
    };
  };

  const getOverlappingSlots = (events, step = 15) => {
    const overlaps = new Set();

    for (let i = 0; i < events.length; i++) {
      for (let j = i + 1; j < events.length; j++) {
        const e1 = events[i];
        const e2 = events[j];

        const start1 = moment(e1.start);
        const end1 = moment(e1.end);
        const start2 = moment(e2.start);
        const end2 = moment(e2.end);

        if (start1.isBefore(end2) && start2.isBefore(end1)) {
          const overlapStart = moment.max(start1, start2).startOf("minute");
          const overlapEnd = moment.min(end1, end2).startOf("minute");

          let current = overlapStart.clone();
          while (current < overlapEnd) {
            overlaps.add(current.format("HH:mm"));
            current.add(step, "minutes");
          }
        }
      }
    }

    return overlaps;
  };

  const CustomAgendaEvent = ({ event }) => {
    return (
      <span>
        <strong>{event.title}</strong>
        <div style={{ fontSize: "12px", color: "#555" }}>
          {event.resource?.status}
        </div>
      </span>
    );
  };

  const CustomGutter = ({ date }) => {
    const timeLabel = moment(date).format("HH:mm");
    const isVisible = overlappingSlots.has(timeLabel);
    return (
      <div
        style={{
          height: "100%",
          visibility: isVisible ? "visible" : "hidden",
          display: isVisible ? "block" : "none",
        }}
      >
        {timeLabel}
      </div>
    );
  };

  useEffect(() => {
    if (meetings && Array.isArray(meetings)) {
      const transformedMeetings = meetings
        ?.filter(
          (meeting) => meeting.status !== "abort" && meeting?.status !== "draft"
        ) // Filter out abort meetings

        .map(transformMeetingToEvent)
        .filter((event) => event !== null);

      const sortedMeetings = transformedMeetings.sort(
        (a, b) => new Date(a.start) - new Date(b.start)
      );

      if (sortedMeetings.length > 0 && currentView === Views.WEEK) {
        const earliestMeeting = sortedMeetings[0];
        const latestMeeting = sortedMeetings[sortedMeetings.length - 1];

        const minTime = new Date(earliestMeeting.start);
        const maxTime = new Date(latestMeeting.end);
        setMin(minTime);
        setMax(maxTime);

        let earliestHr = 24;
        let latestHr = 0;

        const weekStart = new Date(currentStartDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);

        sortedMeetings.forEach((meeting) => {
          const start = new Date(meeting.start);
          const end = new Date(meeting.end);

          if (
            start >= weekStart &&
            end <= weekEnd &&
            start.toDateString() === end.toDateString()
          ) {
            const startHour = start.getHours();
            const endHour = end.getHours();
            earliestHr = Math.min(earliestHr, startHour);
            latestHr = Math.max(latestHr, endHour);
          }
        });

        if (earliestHr !== 24) setEarliestHour(earliestHr);
        if (latestHr !== 0) setLatestHour(latestHr);
      }

      setOverlappingSlots(getOverlappingSlots(sortedMeetings, 15));
      setMyEventsList(sortedMeetings);
    }
  }, [meetings, currentView, currentStartDate]);

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    if (
      event?.resource?.status === "active" ||
      event?.resource?.status === "in_progress" ||
      event?.resource?.status === "to_finish" ||
      event?.resource?.status === "todo"
    ) {
      if (from === "report") {
        window.open(
          `/destination/${event?.resource?.unique_id}/${event?.resource?.id}`,
          "_blank"
        );
      } else {
        navigate(`/invite/${event?.resource?.id}`, {
          state: { from: "meeting" },
        });
      }
    } else {
      if (from === "report") {
        window.open(
          `/destination/${event?.resource?.unique_id}/${event?.resource?.id}`,
          "_blank"
        );
      } else {
        navigate(`/present/invite/${event?.resource?.id}`, {
          state: { from: "meeting" },
        });
      }
    }
  };
  const statuses = [
    { name: t("calendar.status1"), color: "rgb(119 214 113)" },
    { name: t("calendar.status2"), color: "yellow" },
    { name: t("calendar.status3"), color: "red" },
    { name: t("calendar.status4"), color: "rgb(119 19 241)" },
    { name: t("calendar.status5"), color: "rgb(91 170 234)" },
    { name: t("calendar.status6"), color: "#ff9800" },
  ];
  return (
    <>
      <Calendar
        localizer={localizer}
        events={myEventsList}
        onSelectEvent={handleSelectEvent}
        startAccessor="start"
        endAccessor="end"
        views={[Views.WEEK, Views.MONTH, Views.AGENDA]}
        defaultView={Views.WEEK}
        view={currentView} // Controlled view
        onView={handleViewChange} // Handle view changes
        showAllDayEvents={false}
        allDayAccessor={null}
        toolbar={true}
        tooltipAccessor={() => null}
        popup={true}
        allDaySlot={true}
        components={{
          week: {
            header: CustomDateHeader,
            event: CustomEvent,
            timeGutterHeader: CustomGutter,
          },
          agenda: {
            header: CustomAgendaHeader,
            event: CustomAgendaEvent,
          },
          toolbar: (props) => (
            <ReactCalendarToolbar
              {...props}
              view={currentView}
              onView={handleViewChange}
            />
          ),

          eventWrapper: EventWrapper,
        }}
        eventPropGetter={eventPropGetter}
        formats={{
          timeGutterFormat: "HH:mm",
          dayRangeHeaderFormat: "MMMM D, YYYY",
          eventTimeRangeFormat: () => "",
          agendaHeaderFormat: ({ start, end }, culture, localizer) => {
            const sameDay = moment(start).isSame(end, "day");
            if (sameDay) {
              return `${localizer.format(start, "dddd, MMMM D, YYYY")}`;
            }
            return `${localizer.format(
              start,
              "MMMM D, YYYY"
            )} - ${localizer.format(end, "MMMM D, YYYY")}`;
          },
          agendaTimeFormat: "HH:mm",
        }}
        date={currentStartDate}
        onNavigate={(date) => setCurrentStartDate(date)}
        dayLayoutAlgorithm="no-overlap"
        min={minTime}
        max={maxTime}
      />

      <div className="status-legend">
        <div className="status-list">
          {statuses.map((status, index) => (
            <div key={index} className="status-item">
              <span
                className="status-dot"
                style={{ backgroundColor: status.color }}
              ></span>
              {status.name}
            </div>
          ))}
        </div>
      </div>
      {open && <NewMeetingModal open={open} closeModal={handleCloseModal} />}
    </>
  );
};

export default ReactCalendar;
